import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ChatMessage, FlagRecord, InstrumentId, PackConfidence, Reflection, ScenarioResult, TabId, TelemetryEvent, TelemetryType } from '../types/assessment';
import { DEFAULT_INSTRUMENT, getInstrument, instruments } from '../data/instruments';
const TOTAL_SECONDS = 60 * 60;

// One session per instrument, so a LAW-001 run never clobbers a FIN-001 record.
const storageKey = (instrument: InstrumentId) => `lentic.${instrument.toLowerCase()}.v0-1.session`;
const ACTIVE_KEY = 'lentic.activeInstrument';
interface Snapshot {
  participantId: string;
  consented: boolean;
  startedAt: number | null;
  sessionId: string;
  flags: Record<string, FlagRecord>;
  openedDocs: string[];
  sharedDocs: string[];
  dwell: Partial<Record<TabId, number>>;
  messages: ChatMessage[];
  events: TelemetryEvent[];
  reflection: Reflection;
  packConfidence: PackConfidence | null;
  submitted: boolean;
  submittedAt: number | null;
}
const emptySnapshot = (): Snapshot => ({
  participantId: '',
  consented: false,
  startedAt: null,
  sessionId: `S-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
  flags: {},
  openedDocs: [],
  sharedDocs: [],
  dwell: {
    reconciliation: 0,
    budget: 0,
    documents: 0,
    bundle: 0,
    instructions: 0
  },
  messages: [],
  events: [],
  reflection: {
    q1: '',
    q2: '',
    q3: '',
    q4: ''
  },
  packConfidence: null,
  submitted: false,
  submittedAt: null
});
function load(instrument: InstrumentId): Snapshot {
  try {
    const raw = window.localStorage.getItem(storageKey(instrument));
    if (!raw) return emptySnapshot();
    const parsed = JSON.parse(raw) as Snapshot;
    const base = emptySnapshot();
    return {
      ...base,
      ...parsed,
      dwell: {
        ...base.dwell,
        ...(parsed.dwell || {})
      },
      reflection: {
        ...base.reflection,
        ...(parsed.reflection || {})
      },
      sharedDocs: parsed.sharedDocs || []
    };
  } catch {
    return emptySnapshot();
  }
}
function loadActiveInstrument(): InstrumentId {
  try {
    const stored = window.localStorage.getItem(ACTIVE_KEY);
    if (stored && stored in instruments) return stored as InstrumentId;
  } catch {
    /* no-op */
  }
  return DEFAULT_INSTRUMENT;
}
interface AssessmentValue extends Snapshot {
  instrument: InstrumentId;
  remaining: number;
  elapsed: number;
  timeUp: boolean;
  locked: boolean;
  activeTab: TabId;
  thinking: boolean;
  saveState: 'idle' | 'saved';
  promptCount: number;
  results: ScenarioResult[];
  setInstrument: (id: InstrumentId) => void;
  giveConsent: (participantId: string) => void;
  startAssessment: () => void;
  setActiveTab: (tab: TabId) => void;
  openDocument: (docId: string, title: string) => void;
  shareDocument: (docId: string, title: string) => void;
  sendPrompt: (text: string, viaChip?: boolean) => void;
  recordPaste: (text: string) => void;
  saveFlag: (record: FlagRecord) => void;
  removeFlag: (rowId: string) => void;
  setReflection: (r: Reflection) => void;
  setPackConfidence: (c: PackConfidence) => void;
  submit: () => void;
  resetSession: () => void;
}
const AssessmentContext = createContext<AssessmentValue | null>(null);
const uid = () => Math.random().toString(36).slice(2, 10);
export function AssessmentProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [instrument, setInstrumentState] = useState<InstrumentId>(() => loadActiveInstrument());
  const [snap, setSnap] = useState<Snapshot>(() => load(loadActiveInstrument()));
  const [activeTab, setActiveTabState] = useState<TabId>(() => getInstrument(loadActiveInstrument()).defaultTab);
  const [thinking, setThinking] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saved'>('idle');
  const [now, setNow] = useState(() => Date.now());
  const config = getInstrument(instrument);
  const activeTabRef = useRef<TabId>(activeTab);
  activeTabRef.current = activeTab;
  const instrumentRef = useRef<InstrumentId>(instrument);
  instrumentRef.current = instrument;
  const firstRender = useRef(true);

  // Autosave — the session survives a disconnect and the clock keeps running.
  useEffect(() => {
    window.localStorage.setItem(storageKey(instrumentRef.current), JSON.stringify(snap));
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setSaveState('saved');
    const t = window.setTimeout(() => setSaveState('idle'), 1600);
    return () => window.clearTimeout(t);
  }, [snap]);
  const setInstrument = useCallback((id: InstrumentId) => {
    setInstrumentState(prev => {
      if (prev === id) return prev;
      window.localStorage.setItem(ACTIVE_KEY, id);
      setSnap(load(id));
      setActiveTabState(getInstrument(id).defaultTab);
      return id;
    });
  }, []);
  const elapsed = snap.startedAt ? Math.floor((now - snap.startedAt) / 1000) : 0;
  const remaining = Math.max(0, TOTAL_SECONDS - elapsed);
  const timeUp = Boolean(snap.startedAt) && remaining <= 0;
  const locked = snap.submitted;
  useEffect(() => {
    if (!snap.startedAt || snap.submitted) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [snap.startedAt, snap.submitted]);

  // Silent dwell capture — never surfaced to the participant.
  useEffect(() => {
    if (!snap.startedAt || snap.submitted) return;
    const t = window.setInterval(() => {
      setSnap(s => {
        const tab = activeTabRef.current;
        return {
          ...s,
          dwell: {
            ...s.dwell,
            [tab]: (s.dwell[tab] || 0) + 1
          }
        };
      });
    }, 1000);
    return () => window.clearInterval(t);
  }, [snap.startedAt, snap.submitted]);
  const logInto = (s: Snapshot, type: TelemetryType, detail: string): Snapshot => ({
    ...s,
    events: [...s.events, {
      id: uid(),
      at: Date.now(),
      type,
      detail
    }]
  });
  const giveConsent = useCallback((participantId: string) => {
    setSnap(s => logInto({
      ...s,
      participantId: participantId.trim(),
      consented: true
    }, 'consent', `Consent recorded for participant ${participantId.trim()}`));
  }, []);
  const startAssessment = useCallback(() => {
    setSnap(s => s.startedAt ? s : logInto({
      ...s,
      startedAt: Date.now()
    }, 'start', 'Assessment started (60:00)'));
    setNow(Date.now());
  }, []);
  const setActiveTab = useCallback((tab: TabId) => {
    setActiveTabState(prev => {
      if (prev !== tab) setSnap(s => logInto(s, 'tab', `Opened the "${tab}" tab`));
      return tab;
    });
  }, []);
  const openDocument = useCallback((docId: string, title: string) => {
    setSnap(s => logInto({
      ...s,
      openedDocs: s.openedDocs.includes(docId) ? s.openedDocs : [...s.openedDocs, docId]
    }, 'document', `Opened ${docId} — ${title}`));
  }, []);

  // Explicit disclosure to the assistant. Nothing reaches the model without this.
  const shareDocument = useCallback((docId: string, title: string) => {
    setSnap(s => s.sharedDocs.includes(docId) ? s : logInto({
      ...s,
      sharedDocs: [...s.sharedDocs, docId]
    }, 'share', `Shared ${docId} — ${title} with the assistant`));
  }, []);
  const recordPaste = useCallback((text: string) => {
    setSnap(s => logInto(s, 'paste', `Pasted ${text.length} characters into the assistant: "${text.replace(/\s+/g, ' ').slice(0, 90)}…"`));
  }, []);
  const sendPrompt = useCallback((text: string, viaChip = false) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const priorPrompts = snap.messages.filter(m => m.role === 'user').length;
    const shared = snap.sharedDocs;
    setSnap(s => {
      let next: Snapshot = {
        ...s,
        messages: [...s.messages, {
          id: uid(),
          role: 'user',
          content: trimmed,
          at: Date.now()
        }]
      };
      if (viaChip) next = logInto(next, 'chip', `Used suggestion chip: "${trimmed}"`);
      return logInto(next, 'prompt', trimmed.replace(/\s+/g, ' ').slice(0, 140));
    });
    setThinking(true);
    window.setTimeout(() => {
      const reply = config.reply(trimmed, priorPrompts, shared);
      setSnap(s => ({
        ...s,
        messages: [...s.messages, {
          id: uid(),
          role: 'assistant',
          content: reply.content,
          at: Date.now()
        }]
      }));
      setThinking(false);
    }, 700 + Math.min(900, trimmed.length * 3));
  }, [snap.messages, snap.sharedDocs, config]);
  const saveFlag = useCallback((record: FlagRecord) => {
    setSnap(s => logInto({
      ...s,
      flags: {
        ...s.flags,
        [record.rowId]: record
      }
    }, record.kind === 'correction' ? 'correct' : 'flag', `${record.kind === 'correction' ? 'Correction' : 'Flag'} saved on ${record.rowId} · action ${record.actionType} · sources ${record.sources.join(', ') || 'none'} · sign-off ${record.signOff}`));
  }, []);
  const removeFlag = useCallback((rowId: string) => {
    setSnap(s => {
      const flags = {
        ...s.flags
      };
      delete flags[rowId];
      return logInto({
        ...s,
        flags
      }, 'flag', `Entry removed from ${rowId}`);
    });
  }, []);
  const setReflection = useCallback((reflection: Reflection) => {
    setSnap(s => ({
      ...s,
      reflection
    }));
  }, []);
  const setPackConfidence = useCallback((packConfidence: PackConfidence) => {
    setSnap(s => ({
      ...s,
      packConfidence
    }));
  }, []);
  const submit = useCallback(() => {
    setSnap(s => logInto({
      ...s,
      submitted: true,
      submittedAt: Date.now()
    }, 'submit', `Submitted · recommendation "${s.packConfidence}" · ${Object.keys(s.flags).length} entries`));
  }, []);
  const resetSession = useCallback(() => {
    window.localStorage.removeItem(storageKey(instrumentRef.current));
    setSnap(emptySnapshot());
    setActiveTabState(getInstrument(instrumentRef.current).defaultTab);
  }, []);
  const results = useMemo(() => config.evaluate(snap.flags, snap.openedDocs), [snap.flags, snap.openedDocs, config]);
  const value: AssessmentValue = {
    ...snap,
    instrument,
    remaining,
    elapsed,
    timeUp,
    locked,
    activeTab,
    thinking,
    saveState,
    promptCount: snap.messages.filter(m => m.role === 'user').length,
    results,
    setInstrument,
    giveConsent,
    startAssessment,
    setActiveTab,
    openDocument,
    shareDocument,
    sendPrompt,
    recordPaste,
    saveFlag,
    removeFlag,
    setReflection,
    setPackConfidence,
    submit,
    resetSession
  };
  return <AssessmentContext.Provider value={value} data-id="element-404" data-mp="s1ifqxypn1cr">{children}</AssessmentContext.Provider>;
}
export function useAssessment() {
  const ctx = useContext(AssessmentContext);
  if (!ctx) throw new Error('useAssessment must be used inside AssessmentProvider');
  return ctx;
}
export function useInstrumentConfig() {
  const {
    instrument
  } = useAssessment();
  return getInstrument(instrument);
}